/**
 * KeyedOwlAvatar — chroma-keyed owl avatar (Ciocco = green / Ciocca = blue).
 *
 * Higgsfield generated the owl emotion clips on a FLAT per-character key colour
 * (green owl on magenta #FF00FF, blue owl on green #00B140 — the key differs per
 * figure because the owl's own colour must not collide with the key). Alpha
 * video was rejected: it has platform gaps (hvc1-alpha only Safari/iOS, webm-
 * alpha only Chrome/FF). A single flat-key mp4 + canvas chroma-key runs on ALL
 * platforms — the videos are served CORS `*` from arkturian, so drawing them to
 * a canvas and reading pixels is not tainted.
 *
 * This is the reusable transparent-owl building block for Story narration and
 * Knowledge annotations (Alex 2026-07-21: "Eulen für Story und Annotationen").
 * The /live human Dr.-Peter-Ciocco avatar is a separate, opaque set and does NOT
 * use this component.
 *
 * Keying (per Higgsfield): green owl α from `min(R,B) − G` (magenta), blue owl α
 * from `G − max(R,B)` (green), plus despill of the key channel and a soft
 * threshold that feathers the wool edge. The video is drawn to a small canvas
 * sized to the on-screen display (the owl is a disc/overlay, not fullscreen), so
 * the per-pixel JS key stays cheap on mobile.
 */

import { useEffect, useRef, useSyncExternalStore } from 'react'

export type OwlCharacter = 'tschauko' | 'tschauka'
export type OwlState = 'idle' | 'talk' | 'happy' | 'joyful' | 'sad' | 'greeting'

const STORAGE = 'https://api-storage.arkturian.com/storage/media'

/** Owl clip sets (arkturian ids, Higgsfield 2026-07-21). talk = short/med/long. */
const OWL_CLIPS: Record<OwlCharacter, { key: 'magenta' | 'green'; clips: Record<Exclude<OwlState, 'talk'>, number> & { talk: number[] } }> = {
  // Green owl (Ciocco) on flat magenta.
  tschauko: {
    key: 'magenta',
    clips: { idle: 115046, talk: [115047, 115048, 115049], happy: 115050, joyful: 115051, sad: 115052, greeting: 115053 },
  },
  // Blue owl (Ciocca) on flat green.
  tschauka: {
    key: 'green',
    clips: { idle: 115054, talk: [115055, 115056, 115057], happy: 115058, joyful: 115059, sad: 115060, greeting: 115061 },
  },
}

/** Bereits FREIGESTELLTE Standbilder (PNG mit echtem Alpha, 2026-07-24). Sie
 *  sind das Sofortbild und der Rückfall, wenn kein Videobild ankommt — anders
 *  als `owlPosterUrl`, das die reine Key-FARBE liefert und deshalb nie direkt
 *  angezeigt werden darf. */
const OWL_STILLS: Record<OwlCharacter, number> = { tschauko: 115770, tschauka: 115771 }

/**
 * Dasselbe Standbild, aber im RAHMEN DER CLIPS (2026-08-04): aus demselben
 * Idle-Bild bei 0,6 s, mit derselben Key-Mathematik freigestellt, nur nicht
 * eng beschnitten.
 *
 * WARUM ES BEIDE GIBT: Der enge Beschnitt (115770/115771) fuellt einen
 * 52-px-Kreis genau richtig aus — dort saesse die Eule im Clip-Rahmen klein
 * mit viel Luft. Auf der BUEHNE ist es umgekehrt: dort liess der enge
 * Beschnitt die Eule beim ersten Videobild sichtbar schrumpfen.
 *
 * Nachgemessen, Fusslinie als Anteil der Bildhoehe:
 *   eng   115770/115771 → 0,9750
 *   Clips 115046/115054 → 0,8125 / 0,8167
 *   neu   117561/117562 → 0,8146 / 0,8187   (Mitte waagrecht auf 0,002 gleich)
 * Der Rest ist eine halbe Pixelzeile aus dem Verkleinern von 960 auf 480.
 */
const OWL_STAGE_STILLS: Record<OwlCharacter, number> = { tschauko: 117561, tschauka: 117562 }

/**
 * Pruefsummen je Objekt — sie machen die URL INHALTSADRESSIERT.
 *
 * Storage staffelt danach (2026-08-05): mit `?v=<sha256>` gilt die Antwort ein
 * JAHR als unveraenderlich, ohne nur eine Stunde. Der Unterschied ist Absicht
 * — ohne Pruefsumme kann dieselbe Kennung nach einem Bild-Ersetzen andere
 * Bytes liefern, ein Jahr waere dort ein Versprechen, das gebrochen werden
 * koennte. Fuer unsere Besucher in der Schlucht heisst das: ein Rollenwechsel
 * auf einen schon gespielten Clip kostet NULL Bytes statt eines neuen Ladens
 * ueber eine Leitung mit einem Balken.
 *
 * Aendert sich ein Clip, aendert sich die Pruefsumme — dann steht hier eine
 * neue Zeile und die alte URL laeuft aus. Genau so soll es sein.
 */
const OWL_SHA: Record<number, string> = {
  115046: '894c0fcee1f0f3c2301e6b5bb1333d144620a30bf3ae1874167bd9cc97ef8503',
  115047: 'ad8efc6e16920e727d5cc65fa9f95cf61e12a6fded2daa295f2168cad8143bf1',
  115048: 'c5dd622f33fd7f6866a38eef0591fb04536b29de2110d0423bf4dd6d6bd0410d',
  115049: '6e7cef3030d7156b3f4aff169ed64ad60efd7828bbc01baf598a6d0477a86924',
  115050: '72b639908833d8075b3c1f78a033a315d66739fba50dddab1d621b5beba8faa9',
  115051: '3da618f200d276c2f8099026bff539bbed01c784bfa0e47ecc672a91fd7355d3',
  115052: '27b9e23b9a9123ebeadb33a837a6445a0ba5acfa9a41552a245f7edf5d97681f',
  115053: 'e06cd7b27c7010a7dbd0c930d49e52520df0ede93b2e5da9c6695f7545ac4c40',
  115054: '31bcbbb52f9dbbe74b1d654f4c7b4bb2216bab51c32e20563940b13d53118af0',
  115055: 'f7c1c83cbe4d9b1010f8fca900e3bf559e1aee49aaee2219c3d319ba35e92a44',
  115056: 'a71d763550fdcd2bbf89ad112fbf71d8708f7a0e0af93824fa75305ff25c2fcb',
  115057: 'f3f93c8c9c0feb687f4762c8f4a52d3be9d71a28e5020b4e14fe023181576e7b',
  115058: 'a0446cbe00aa87b89f50544ea57eec9c07b2ee5cd8a6fc147a81614c004a035b',
  115059: '17f3e3e8de9e8617ca730575ad154fac660e521ba3227fc29be7bca414eeeb84',
  115060: 'c1c92e714286696378170cda9a5b353d3f5b9943f612ed0273ccbb178e85593d',
  115061: '52f227d440802ef6f20d8951fa274cad932f278cbe7bae97be1c82b5c897c36f',
  // Standbilder: eng beschnitten fuer kleine Kreise, Clip-Rahmen fuer die Buehne.
  115770: '5c1db520045043a55dc9345d571bc394eb372d39ec560873f8d909038c0648e9',
  115771: '369d05e1032c9568540759b7535fa61670c13745c2af36577f8461b5a8a9c017',
  117561: '9a368dabcf0c62bbba616e360e74faea58e0087f8b5841f9fbda70491d2231c3',
  117562: 'df96a1f8baa723aa1a8b5785dce23263b443a4f581c49d13a332c9457e65e5aa',
}

/** `?v=` anhaengen, wenn wir die Pruefsumme kennen — sonst die nackte URL. */
function withSha(id: number, query = ''): string {
  const v = OWL_SHA[id]
  const sep = query ? '&' : '?'
  return `${STORAGE}/${id}${query}${v ? `${sep}v=${v}` : ''}`
}

export function owlClipUrl(character: OwlCharacter, state: OwlState, talkVariant = 0): string {
  const set = OWL_CLIPS[character]
  const id = state === 'talk' ? set.clips.talk[talkVariant % set.clips.talk.length] : set.clips[state]
  return withSha(id)
}

/** Freigestelltes Standbild der Figur — anzeigefertig, mit Alpha. */
export function owlStillUrl(character: OwlCharacter, width = 480): string {
  return withSha(OWL_STILLS[character], `?width=${width}&format=png`)
}

/** Standbild fuer die BUEHNE — gleich gerahmt wie die Clips, siehe oben.
 *  Fuer kleine Kreise/Badges NICHT verwenden: dort gilt `owlStillUrl`. */
export function owlStageStillUrl(character: OwlCharacter, width = 480): string {
  return withSha(OWL_STAGE_STILLS[character], `?width=${width}&format=png`)
}

/** Poster/still = an idle frame (video → jpg), used before the first keyed frame. */
export function owlPosterUrl(character: OwlCharacter): string {
  return `${STORAGE}/${OWL_CLIPS[character].clips.idle}?variant=thumbnail&format=jpg`
}

interface Props {
  character: OwlCharacter
  state?: OwlState
  talkVariant?: number
  /** Rendered square size in CSS px (also the keying resolution — kept small). */
  size?: number
  /** Wie zwischen Clips geschnitten wird. Vorgabe: an der Clip-Grenze. */
  switchMode?: OwlSwitchMode
  /**
   * Das erste sichtbare gekeyte Bild. Der Realtime-Controller startet die
   * Begruessung erst darauf — ohne diese Meldung gruesst die Figur nie.
   */
  onFirstFrame?: () => void
  /**
   * Ein Einmal-Zustand ist durchgelaufen (`greeting`, `happy`, `joyful`,
   * `sad`). Damit gibt die Figur die Rolle an die Zustandsmaschine zurueck.
   * Ohne die Meldung bliebe sie in ihr stehen.
   */
  onClipEnded?: (state: OwlState) => void
  /**
   * Bildausschnitt. 1 = ganzes Quadrat wie erzeugt, groesser = naeher heran
   * (Alex 2026-08-04: „dass das grundsaetzlich ein bisschen weiter
   * herangezogen ist"). Verankert an der UNTERKANTE: die Eule sitzt auf
   * ihren Fuessen, sie darf beim Heranziehen nicht davonschweben.
   */
  zoom?: number
  /**
   * Sichtbares Zeichen, dass die Figur SPRICHT. Die Clips sind fertige
   * Videos — ihr Schnabel bewegt sich kaum, und nachanimieren laesst er sich
   * nicht. Das Zeichen folgt deshalb dem Ton (siehe `speechLevel`).
   */
  speechCue?: OwlSpeechCue
  /**
   * Abfrage des Sprech-Pegels (0…1), einmal je Bild. BEWUSST eine Funktion
   * und keine Zahl: als Eigenschaft waeren das 60 Neuzeichnungen je Sekunde.
   */
  speechLevel?: () => number
  className?: string
  style?: React.CSSProperties
}

/** Dauer der Überblendung — nur im Modus 'immediate' überhaupt sichtbar. */
const FADE_MS = 260

/**
 * Wie ein Zustandswechsel geschnitten wird.
 *
 * 'finish-clip' — laufenden Durchlauf zu Ende spielen, dann wechseln. Weil
 *   jeder Eulen-Clip so endet, wie er beginnt, ist der Schnitt dort
 *   unsichtbar. Vorgabe für die Eulen (Alex 2026-08-04).
 * 'immediate' — sofort wechseln, mit kurzer Blende. Für Fälle, in denen nicht
 *   gewartet werden kann.
 */
export type OwlSwitchMode = 'finish-clip' | 'immediate'

/**
 * Wie sichtbar gemacht wird, dass die Figur spricht.
 *
 * 'none'  — nichts (Vorgabe; alle bisherigen Einsatzorte bleiben, wie sie sind)
 * 'pulse' — die Figur atmet leicht mit der Lautstaerke mit. Sehr dezent.
 * 'puffs' — dazu kleine Woelkchen, die am Schnabel aufsteigen, solange
 *           gesprochen wird (Alex: „koennte das sein, Rauchwolken").
 */
export type OwlSpeechCue = 'none' | 'pulse' | 'puffs'

/** Ab diesem Pegel gilt die Figur als sprechend. */
const SPEECH_THRESHOLD = 0.08

/**
 * Wo der Schnabel sitzt, in Anteilen des Figur-Kastens.
 *
 * GEMESSEN, nicht geschaetzt (2026-08-04): orange Bildpunkte im
 * freigestellten Standbild, nach Hoehenbaendern getrennt. Der erste Versuch
 * hatte die orangen FUESSE mitgezaehlt (Baender bei 92–98 %) und den Ursprung
 * dadurch zu tief gelegt; der Schnabel liegt bei 32–40 % Hoehe.
 */
const OWL_BEAK: Record<OwlCharacter, { x: number; y: number }> = {
  tschauko: { x: 0.54, y: 0.36 },
  tschauka: { x: 0.51, y: 0.36 },
}


/**
 * Zwei Bildquellen, eine Leinwand — der Grund steht in `setSource`.
 *
 * Rahmenwerk-frei gehalten (kein React), damit die Zeichenschleife nicht bei
 * jedem Zustandswechsel neu aufgesetzt werden muss: genau dieses Neu-Aufsetzen
 * war der sichtbare Sprung.
 */
class OwlFader {
  private readonly ctx: CanvasRenderingContext2D
  private readonly panels: Array<{
    video: HTMLVideoElement
    buffer: HTMLCanvasElement
    bufferCtx: CanvasRenderingContext2D
    src: string | null
  }>
  private active = 0
  private fadeStart = 0
  private fading = false
  private raf = 0
  private stopped = false
  private hasVideoFrame = false
  private gestureBound = false
  // Offene Wechsel-Absicht (Modus 'finish-clip').
  private pendingTimer: ReturnType<typeof setTimeout> | null = null
  private pendingEnded: (() => void) | null = null
  private pendingEndedOn: HTMLVideoElement | null = null
  private pendingLoaded: (() => void) | null = null
  /** Faellt der Schnitt NICHT auf die Clip-Grenze, wird ueberblendet. */
  private pendingHard = false
  // ── Darstellung (zur Laufzeit aenderbar, ohne Neuaufbau) ──
  private zoom = 1
  private cue: OwlSpeechCue = 'none'
  private levelFn: (() => number) | null = null
  private smoothLevel = 0
  private puffs: Array<{ x: number; y: number; r: number; life: number; vx: number }> = []
  private lastPuffAt = 0
  private readonly still = new Image()

  // Explizite Felder statt Konstruktor-Kurzschreibweise: `erasableSyntaxOnly`
  // verbietet Parameter-Eigenschaften.
  private readonly canvas: HTMLCanvasElement
  private readonly character: OwlCharacter
  private readonly px: number
  /** Rueckmeldungen an die Zustandsmaschine (siehe `setLifecycle`). */
  private onFirstFrame: (() => void) | null = null
  private firstFrameReported = false
  private clipEnded: (() => void) | null = null
  private clipEndedOn: HTMLVideoElement | null = null

  constructor(
    canvas: HTMLCanvasElement,
    videos: [HTMLVideoElement, HTMLVideoElement],
    character: OwlCharacter,
    px: number,
  ) {
    this.canvas = canvas
    this.character = character
    this.px = px
    canvas.width = px
    canvas.height = px
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('KeyedOwlAvatar: kein 2D-Kontext')
    this.ctx = ctx

    this.panels = videos.map((video) => {
      video.crossOrigin = 'anonymous'
      video.muted = true
      video.playsInline = true
      ;(video as HTMLVideoElement & { webkitPlaysInline?: boolean }).webkitPlaysInline = true
      video.preload = 'auto'
      const buffer = document.createElement('canvas')
      buffer.width = px
      buffer.height = px
      const bufferCtx = buffer.getContext('2d', { willReadFrequently: true })!
      return { video, buffer, bufferCtx, src: null }
    })

    // Sofortbild: das freigestellte Standbild, bis das erste Videobild da ist.
    this.still.crossOrigin = 'anonymous'
    this.still.onload = () => {
      if (this.stopped || this.hasVideoFrame) return
      try { this.drawStill() } catch { /* dekorativ */ }
    }
    this.still.src = owlStageStillUrl(character, Math.min(480, px))

    this.raf = requestAnimationFrame(this.draw)
  }

  /**
   * Neue Quelle anmelden.
   *
   * ZWEI MODI (Alex 2026-08-04): Der allgemeine Player der Menschen-Figur
   * (`GreenScreenCanvasCrossfader`) schneidet hart zwischen Clips und sieht
   * dort gut aus — der bleibt unangetastet. Die EULEN brauchen etwas anderes,
   * und zwar aus einem messbaren Grund: alle 16 Eulen-Clips beginnen UND
   * enden mit derselben Figurgrösse (Tschauka 64,2 % Bildhöhe, Tschauko
   * 62,5 %), bewegen sich aber dazwischen weit — „sad" duckt sich auf 53 %,
   * „joyful" reckt sich auf 70 %. Ein Schnitt mitten in dieser Bewegung
   * zeigt den Unterschied als Sprung (gemessen: 30 % Bildhöhe zwischen zwei
   * Bildern).
   *
   *   'finish-clip' (Vorgabe): den laufenden Durchlauf ZU ENDE spielen, dann
   *      wechseln. An der Clip-Grenze sind Ende und Anfang gleich — der
   *      Schnitt ist dort per Konstruktion unsichtbar, ganz ohne Blende.
   *   'immediate': sofort wechseln, mit kurzer Blende als Notnagel. Greift
   *      auch dann, wenn im Modus 'finish-clip' kein Ende eintrifft (Video
   *      angehalten, Stromsparmodus) — sonst bliebe die Figur im alten
   *      Zustand stehen.
   */
  setSource(url: string, loop: boolean, mode: OwlSwitchMode = 'finish-clip', onEnded?: () => void): void {
    if (this.stopped) return
    const current = this.panels[this.active]
    this.clearPending()
    this.clearClipEnded()

    if (current.src === url) {
      // Ziel ist der laufende Clip — eine zuvor abgeschaltete Wiederholung
      // wieder einschalten, sonst bliebe er nach einem Durchlauf stehen.
      current.video.loop = loop
      this.tryPlay(current.video)
      return
    }

    const incomingIndex = this.active === 0 ? 1 : 0
    const incoming = this.panels[incomingIndex]
    incoming.src = url
    incoming.video.loop = loop
    incoming.video.pause()
    incoming.video.src = url
    incoming.video.load()

    const commit = () => {
      if (this.stopped || this.panels[incomingIndex].src !== url) return
      this.clearPending()
      // Ohne bisheriges Bild gibt es nichts zu überblenden — dann sofort.
      this.fading = this.pendingHard && current.src != null && this.hasVideoFrame
      this.fadeStart = performance.now()
      try { incoming.video.currentTime = 0 } catch { /* noch nicht durchsuchbar */ }
      this.tryPlay(incoming.video)
      this.active = incomingIndex
      // Einmal-Clips (Begruessung, Gefuehlsregung) geben die Rolle zurueck,
      // sobald sie durchgelaufen sind — sonst bliebe die Zustandsmaschine in
      // dieser Rolle stehen und die Eule wiederholte sie endlos.
      if (onEnded) {
        this.clipEnded = () => { this.clipEnded = null; this.clipEndedOn = null; onEnded() }
        incoming.video.addEventListener('ended', this.clipEnded, { once: true })
        this.clipEndedOn = incoming.video
      }
    }
    const whenLoaded = (run: () => void) => {
      if (incoming.video.readyState >= 2) run()
      else {
        this.pendingLoaded = run
        incoming.video.addEventListener('loadeddata', run, { once: true })
      }
    }

    const runningLoop = !current.video.paused && current.src != null && this.hasVideoFrame
    if (mode === 'immediate' || !runningLoop) {
      this.pendingHard = mode === 'immediate'
      whenLoaded(commit)
      return
    }

    // 'finish-clip': den laufenden Durchlauf auslaufen lassen. Die
    // Wiederholung wird dafür abgeschaltet, damit 'ended' überhaupt kommt.
    this.pendingHard = false
    current.video.loop = false
    this.pendingEnded = () => whenLoaded(commit)
    current.video.addEventListener('ended', this.pendingEnded, { once: true })
    this.pendingEndedOn = current.video

    // Sicherheitsnetz: bleibt das Ende aus (angehaltenes Video, Stromspar-
    // modus), nach der Restlaufzeit plus Puffer trotzdem wechseln — dann mit
    // Blende, weil der Schnitt an beliebiger Stelle fiele.
    const restMs = Number.isFinite(current.video.duration)
      ? Math.max(0, (current.video.duration - current.video.currentTime) * 1000)
      : 5000
    this.pendingTimer = setTimeout(() => {
      this.pendingHard = true
      whenLoaded(commit)
    }, restMs + 900)
  }

  /** Darstellung nachstellen — Ausschnitt und Sprech-Zeichen. */
  setPresentation(zoom: number, cue: OwlSpeechCue, levelFn: (() => number) | null): void {
    this.zoom = Number.isFinite(zoom) && zoom > 0 ? zoom : 1
    this.cue = cue
    this.levelFn = levelFn
  }

  /** Rueckmeldung des ersten sichtbaren Bildes anmelden. */
  setLifecycle(onFirstFrame: (() => void) | null): void {
    this.onFirstFrame = onFirstFrame
    // Laeuft die Figur schon, ist das Signal sonst fuer immer verpasst.
    if (onFirstFrame && this.hasVideoFrame && !this.firstFrameReported) {
      this.firstFrameReported = true
      onFirstFrame()
    }
  }

  private clearClipEnded(): void {
    if (this.clipEnded && this.clipEndedOn) {
      this.clipEndedOn.removeEventListener('ended', this.clipEnded)
    }
    this.clipEnded = null
    this.clipEndedOn = null
  }

  /** Alle offenen Wechsel-Absichten fallen lassen. */
  private clearPending(): void {
    if (this.pendingTimer != null) { clearTimeout(this.pendingTimer); this.pendingTimer = null }
    if (this.pendingEnded && this.pendingEndedOn) {
      this.pendingEndedOn.removeEventListener('ended', this.pendingEnded)
    }
    this.pendingEnded = null
    this.pendingEndedOn = null
    if (this.pendingLoaded) {
      for (const panel of this.panels) panel.video.removeEventListener('loadeddata', this.pendingLoaded)
      this.pendingLoaded = null
    }
  }

  /**
   * Abspielen versuchen; wird es abgewiesen (iOS-Stromsparmodus, strenge
   * Autoplay-Regeln), bei der ERSTEN Berührung erneut versuchen. Bis dahin
   * steht das freigestellte Standbild — die Figur ist nie unsichtbar.
   */
  private tryPlay(video: HTMLVideoElement): void {
    void video.play().catch(() => {
      if (this.stopped || this.gestureBound) return
      this.gestureBound = true
      window.addEventListener('pointerdown', this.retryOnGesture, { once: true })
      window.addEventListener('keydown', this.retryOnGesture, { once: true })
    })
  }

  private readonly retryOnGesture = (): void => {
    this.gestureBound = false
    window.removeEventListener('pointerdown', this.retryOnGesture)
    window.removeEventListener('keydown', this.retryOnGesture)
    if (this.stopped) return
    void this.panels[this.active].video.play().catch(() => { /* Standbild bleibt */ })
  }

  /** Zeichnet ein Videobild gekeyt in den Puffer des Panels. */
  private keyInto(panel: OwlFader['panels'][number]): boolean {
    const { video, bufferCtx } = panel
    if (video.readyState < 2 || !video.videoWidth) return false
    const px = this.px
    bufferCtx.drawImage(video, 0, 0, px, px)
    // Ein Lesefehler (etwa eine „vergiftete" Leinwand, wenn ein Zwischen-
    // speicher die CORS-Kopfzeile schluckt) darf die Schleife nicht beenden.
    let img: ImageData
    try {
      img = bufferCtx.getImageData(0, 0, px, px)
    } catch {
      return false
    }
    const keyKind = OWL_CLIPS[this.character].key
    const d = img.data
    for (let i = 0; i < d.length; i += 4) {
      const r = d[i], g = d[i + 1], b = d[i + 2]
      // Per-character key metric: high metric = key background → transparent.
      const metric = keyKind === 'magenta'
        ? Math.min(r, b) - g          // magenta bg: R,B high, G low
        : g - Math.max(r, b)          // green bg:  G high, R,B low
      // Soft threshold → feathered wool edge (t0 fully opaque, t1 fully keyed).
      const t0 = 20, t1 = 70
      let alpha = 255
      if (metric >= t1) alpha = 0
      else if (metric > t0) alpha = Math.round(255 * (1 - (metric - t0) / (t1 - t0)))
      d[i + 3] = alpha
      // Despill: on semi/edge pixels pull the key channel back so no colour
      // fringe survives (magenta → cap R,B to G; green → cap G to max(R,B)).
      if (alpha > 0 && metric > 0) {
        if (keyKind === 'magenta') {
          if (r > g) d[i] = g
          if (b > g) d[i + 2] = g
        } else {
          const mx = Math.max(r, b)
          if (g > mx) d[i + 1] = mx
        }
      }
    }
    bufferCtx.putImageData(img, 0, 0)
    return true
  }

  /**
   * Das Standbild an der Stelle zeichnen, an der auch die Videofigur steht.
   * Ohne diese Einpassung springt die Figur beim ersten Videobild.
   */
  /**
   * Das Standbild fuellt denselben Rahmen wie die Clips — deshalb ohne jede
   * Korrektur. Bis 2026-08-04 stand hier eine gemessene Tabelle aus scale/
   * top/left je Figur, weil das damalige Standbild enger beschnitten war und
   * die Eule beim ersten Videobild sichtbar schrumpfte (Bildschirmfotos
   * 117433/117434). Ein richtig gerahmtes Bild macht die Tabelle ueberfluessig.
   */
  private drawStill(): void {
    const [dx, dy, dw, dh] = this.destRect(this.zoom)
    this.ctx.drawImage(this.still, dx, dy, dw, dh)
  }

  /**
   * Zielrechteck fuer den Ausschnitt.
   *
   * Der Massstab haengt an der UNTERKANTE, nicht an der Mitte: die Eule sitzt
   * auf ihren Fuessen. Waere die Mitte der Anker, waeren die Fuesse beim
   * Heranziehen aus dem Bild gewandert und die Figur haette geschwebt.
   * Waagrecht wird mittig beschnitten.
   */
  private destRect(scale: number): [number, number, number, number] {
    const px = this.px
    const w = px * scale
    const h = px * scale
    return [(px - w) / 2, px - h, w, h]
  }

  private readonly draw = (): void => {
    if (this.stopped) return
    const px = this.px
    const activePanel = this.panels[this.active]
    const drewActive = this.keyInto(activePanel)
    if (drewActive) {
      const t = this.fading ? Math.min(1, (performance.now() - this.fadeStart) / FADE_MS) : 1
      if (t >= 1) this.fading = false

      // Sprech-Pegel geglaettet: der Rohwert zappelt je Bild, das saehe
      // zittrig aus. Schnell hoch, langsam runter — wie ein Pegelausschlag.
      const raw = this.cue === 'none' ? 0 : (this.levelFn?.() ?? 0)
      this.smoothLevel = raw > this.smoothLevel
        ? this.smoothLevel + (raw - this.smoothLevel) * 0.45
        : this.smoothLevel + (raw - this.smoothLevel) * 0.12
      const speaking = this.smoothLevel > SPEECH_THRESHOLD
      // Prüf-Haken: `window.__owlDebug = true` im Browser legt Pegel und
      // Zeichen offen. Kostet im Normalbetrieb einen Vergleich je Bild und
      // erspart im Fehlerfall das Raten (2026-08-04: die Wölkchen blieben
      // aus, und von aussen war nicht zu sehen, ob der Pegel oder das
      // Zeichnen schuld war).
      if ((window as unknown as { __owlDebug?: boolean }).__owlDebug) {
        ;(window as unknown as { __owl?: unknown }).__owl = {
          cue: this.cue, zoom: this.zoom, raw, level: +this.smoothLevel.toFixed(3),
          speaking, puffs: this.puffs.length, hasLevelFn: this.levelFn != null,
        }
      }
      // „Atmen" mit der Stimme: hoechstens 3,5 % — mehr wirkt wie Wackeln.
      const breath = this.cue === 'none' ? 0 : Math.min(0.035, this.smoothLevel * 0.06)
      const scale = this.zoom * (1 + breath)

      this.ctx.clearRect(0, 0, px, px)
      const [dx, dy, dw, dh] = this.destRect(scale)
      if (this.fading) {
        const outgoing = this.panels[this.active === 0 ? 1 : 0]
        if (this.keyInto(outgoing)) {
          this.ctx.globalAlpha = 1 - t
          this.ctx.drawImage(outgoing.buffer, dx, dy, dw, dh)
        }
      }
      this.ctx.globalAlpha = this.fading ? t : 1
      this.ctx.drawImage(activePanel.buffer, dx, dy, dw, dh)
      this.ctx.globalAlpha = 1
      if (this.cue === 'puffs') this.drawPuffs(speaking, dx, dy, dw, dh)
      this.hasVideoFrame = true
      // Das ERSTE gekeyte Bild ist das Startsignal der Zustandsmaschine: der
      // Controller laesst die Begruessung erst laufen, wenn die Figur wirklich
      // sichtbar ist (sonst spraeche sie in ein leeres Bild). Fuer den
      // Menschen meldet das der Green-Canvas-Renderer — die Eule hat es bis
      // heute nie gemeldet und deshalb NIE gegruesst.
      if (!this.firstFrameReported) {
        this.firstFrameReported = true
        this.onFirstFrame?.()
      }
    }
    this.raf = requestAnimationFrame(this.draw)
  }

  /**
   * Woelkchen am Schnabel, solange gesprochen wird.
   *
   * Sie treten SEITLICH aus und ziehen schraeg weg. Klein, blass, kurzlebig:
   * das Zeichen soll auffallen, ohne die Figur zu verdecken.
   */
  private drawPuffs(speaking: boolean, dx: number, dy: number, dw: number, dh: number): void {
    const now = performance.now()
    const beak = OWL_BEAK[this.character]
    const beakX = dx + dw * beak.x
    const beakY = dy + dh * beak.y
    // Der Abstand richtet sich nach der Lautstaerke: lauter = dichter.
    const gap = 300 - Math.min(190, this.smoothLevel * 480)
    if (speaking && now - this.lastPuffAt > gap && this.puffs.length < 10) {
      this.lastPuffAt = now
      // Ein Atemstrom hat EINE Richtung. Die fruehere Abwechslung links /
      // rechts sah aus, als kaemen die Woelkchen aus beiden Gesichtshaelften
      // statt aus dem Schnabel (Alex 2026-08-16). Rechts ist die freie Seite
      // der kanonischen Eulen-Pose; dadurch bleibt auch das Gesicht unverdeckt.
      const side = 1
      this.puffs.push({
        x: beakX + side * dw * (0.07 + Math.random() * 0.02),
        y: beakY + (Math.random() - 0.5) * dh * 0.02,
        r: dw * (0.022 + this.smoothLevel * 0.018),
        life: 1,
        vx: side * dw * 0.0026,
      })
    }
    const ctx = this.ctx
    for (const puff of this.puffs) {
      puff.life -= 0.03
      puff.y -= dh * 0.0018
      puff.x += puff.vx * 0.8
      puff.r *= 1.008
      if (puff.life <= 0) continue
      ctx.globalAlpha = Math.max(0, puff.life) * 0.5
      ctx.beginPath()
      ctx.arc(puff.x, puff.y, puff.r, 0, Math.PI * 2)
      ctx.fillStyle = '#ffffff'
      ctx.fill()
    }
    ctx.globalAlpha = 1
    this.puffs = this.puffs.filter((p) => p.life > 0)
  }

  dispose(): void {
    this.stopped = true
    this.clearPending()
    this.clearClipEnded()
    this.onFirstFrame = null
    cancelAnimationFrame(this.raf)
    window.removeEventListener('pointerdown', this.retryOnGesture)
    window.removeEventListener('keydown', this.retryOnGesture)
    this.still.onload = null
    for (const panel of this.panels) {
      panel.video.pause()
      panel.video.removeAttribute('src')
      panel.video.load()
    }
  }
}

/* ── Wo steht gerade eine Eule? ─────────────────────────────────────────────
 *
 * Alex, 2026-08-11: „immer, wenn er die Eule irgendwo auf der Page anzeigt,
 * soll er sie nicht im Thumbnail anzeigen" — sonst steht sie zweimal da und
 * „wirkt doof".
 *
 * Die Frage „ist schon eine Eule zu sehen?" beantwortet am ehrlichsten die
 * Eule selbst: jede gezeichnete meldet sich an, solange sie im Baum haengt.
 * Der Kreis im Kopf des Blatts (ein Standbild, keine gezeichnete Eule) fragt
 * hier nach und bleibt leer, solange irgendwo eine echte laeuft — auf der
 * Karte, im Bild, am 3D-Pin oder im Abspieler.
 *
 * Vorher stand dafuer EIN handgereichtes Flag („die grosse Eule steht auf der
 * Karte"). Es kannte genau einen der vier Orte; die anderen drei liefen als
 * Doppelung durch. Ein Zaehler an der Quelle kennt alle vier, auch die, die
 * es morgen gibt.
 */
let sichtbareEulen = 0
const eulenHoerer = new Set<() => void>()
function eulenStandMelden(): void { for (const h of eulenHoerer) h() }

/** True, sobald IRGENDWO eine gezeichnete Eule im Baum haengt. */
export function useOwlOnScreen(): boolean {
  return useSyncExternalStore(
    (cb) => { eulenHoerer.add(cb); return () => { eulenHoerer.delete(cb) } },
    () => sichtbareEulen > 0,
    () => false,   // auf dem Server steht keine
  )
}

export default function KeyedOwlAvatar({
  character, state = 'idle', talkVariant = 0, size, switchMode = 'finish-clip',
  zoom = 1, speechCue = 'none', speechLevel, className, style,
  onFirstFrame, onClipEnded,
}: Props) {
  // An- und Abmelden am Praesenz-Zaehler (siehe `useOwlOnScreen`). Bewusst
  // ohne Abhaengigkeiten: gezaehlt wird das Dasein, nicht der Zustand.
  useEffect(() => {
    sichtbareEulen += 1
    eulenStandMelden()
    return () => { sichtbareEulen = Math.max(0, sichtbareEulen - 1); eulenStandMelden() }
  }, [])

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const videoARef = useRef<HTMLVideoElement | null>(null)
  const videoBRef = useRef<HTMLVideoElement | null>(null)
  const faderRef = useRef<OwlFader | null>(null)

  // Aufbau je Figur und Puffergrösse. Der ZUSTAND steckt bewusst NICHT in den
  // Abhängigkeiten: sonst würde die Zeichenschleife bei jedem Wechsel neu
  // aufgesetzt — und genau dieser Neustart war der harte Schnitt.
  useEffect(() => {
    const canvas = canvasRef.current
    const a = videoARef.current
    const b = videoBRef.current
    if (!canvas || !a || !b) return
    // Keying-buffer resolution — DECOUPLED from the on-screen display size (the
    // display comes from `size`/`className` CSS). The owl is small on screen, so
    // a modest buffer keeps the per-pixel JS key cheap on phones; capped at 480.
    const dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1)
    const px = Math.min(480, Math.round((size ?? 240) * dpr))
    const fader = new OwlFader(canvas, [a, b], character, px)
    faderRef.current = fader
    return () => {
      faderRef.current = null
      fader.dispose()
    }
  }, [character, size])

  // Darstellung: Ausschnitt und Sprech-Zeichen sind zur Laufzeit stellbar —
  // sie brauchen keinen Neuaufbau der Zeichenschleife.
  const levelRef = useRef(speechLevel)
  levelRef.current = speechLevel
  useEffect(() => {
    faderRef.current?.setPresentation(zoom, speechCue, () => levelRef.current?.() ?? 0)
  }, [zoom, speechCue, character, size])

  // Rueckmeldungen als Referenz halten: sie duerfen die Zeichenschleife nicht
  // neu aufsetzen, nur weil der Aufrufer eine neue Funktion gebaut hat.
  const firstFrameRef = useRef(onFirstFrame)
  firstFrameRef.current = onFirstFrame
  const clipEndedRef = useRef(onClipEnded)
  clipEndedRef.current = onClipEnded

  useEffect(() => {
    faderRef.current?.setLifecycle(() => firstFrameRef.current?.())
  }, [character, size])

  // Zustandswechsel: nur die Quelle tauschen.
  useEffect(() => {
    // Einmal-Zustaende — dieselbe Menge wie beim Menschen-Avatar
    // (ArcturianFullbodyAvatar): sie laufen EINMAL und geben dann die Rolle
    // zurueck. Vorher liefen `happy`/`joyful`/`sad` bei der Eule endlos, weil
    // nur `greeting` als nicht-wiederholend galt.
    const oneShot = state === 'greeting' || state === 'happy' || state === 'joyful' || state === 'sad'
    faderRef.current?.setSource(
      owlClipUrl(character, state, talkVariant),
      !oneShot,
      switchMode,
      oneShot ? () => clipEndedRef.current?.(state) : undefined,
    )
  }, [character, state, talkVariant, size, switchMode])

  return (
    <>
      <canvas
        ref={canvasRef}
        className={className}
        aria-hidden="true"
        // Display size: inline `size` when given (standalone use), otherwise the
        // className's CSS drives it (e.g. the responsive .fv-*__mascot boxes).
        style={{ display: 'block', ...(size != null ? { width: size, height: size } : {}), ...style }}
      />
      {/* Die Bildquellen. Sie MÜSSEN im Baum hängen (iOS dekodiert ein nicht
          eingehängtes <video> nicht für `drawImage`) und dürfen NICHT per
          `display:none`/`visibility:hidden` verschwinden — beides hält die
          Dekodierung ebenfalls an. Zwei Bildpunkte am unteren Rand, nahezu
          durchsichtig und nicht anfassbar, sind der kleinste Kompromiss, der
          auf allen Geräten Bilder liefert. Zwei Elemente, weil der Wechsel
          überblendet wird und dafür beide Clips gleichzeitig laufen. */}
      {[videoARef, videoBRef].map((ref, i) => (
        <video
          key={i}
          ref={ref}
          aria-hidden="true"
          tabIndex={-1}
          muted
          playsInline
          preload="auto"
          style={{
            position: 'fixed', left: 0, bottom: 0, width: 2, height: 2,
            opacity: 0.01, pointerEvents: 'none', zIndex: -1,
          }}
        />
      ))}
    </>
  )
}
